import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Download, FileText, Pencil, Trash2 } from 'lucide-react';
import {
    useProject,
    useProjectComments,
    useAddProjectComment,
    useDeleteProject,
} from '../hooks/useApi';
import TaskCard from '../components/common/TaskCard';
import ProjectModal from '../components/ProjectModal';
import { fileUrl } from '../services/api';
import {
    Avatar,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    ConfirmDialog,
    DetailRow,
    EmptyState,
    ErrorState,
    ListSkeleton,
    PageHeader,
    PriorityBadge,
    ProgressBar,
    Skeleton,
    StatusBadge,
    Tabs,
} from '../components/ui';

const TABS = [
    { value: 'overview', label: 'Overview' },
    { value: 'tasks', label: 'Tasks' },
    { value: 'team', label: 'Team' },
    { value: 'documents', label: 'Documents' },
    { value: 'comments', label: 'Comments' },
];

const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === '1970-01-01') return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime()) || date.getFullYear() === 1970) return '—';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const ProjectDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: project, isLoading, isError } = useProject(id);
    const { data: comments, isLoading: commentsLoading } = useProjectComments(id);
    const addCommentMutation = useAddProjectComment();
    const deleteProject = useDeleteProject();

    const [activeTab, setActiveTab] = useState('overview');
    const [commentText, setCommentText] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const handleAddComment = (e) => {
        e.preventDefault();
        if (!commentText.trim()) return;
        addCommentMutation.mutate({ id, comment: commentText }, { onSuccess: () => setCommentText('') });
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteProject.mutateAsync(id);
            setIsDeleteModalOpen(false);
            navigate('/projects');
        } catch (error) {
            console.error('Error deleting project:', error);
        }
    };

    if (isLoading) {
        return (
            <>
                <Skeleton w={260} h={26} className="mb-5" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Card className="lg:col-span-2">
                        <CardBody>
                            <ListSkeleton rows={5} />
                        </CardBody>
                    </Card>
                    <Card>
                        <CardBody>
                            <ListSkeleton rows={3} />
                        </CardBody>
                    </Card>
                </div>
            </>
        );
    }

    if (isError || !project) {
        return (
            <Card>
                <ErrorState
                    title="Project not found"
                    description="It may have been deleted, or you may not have access to it."
                    onRetry={() => navigate('/projects')}
                />
            </Card>
        );
    }

    const tasks = project.tasks || [];
    const doneTasks = tasks.filter((t) => t.status === 'done').length;

    return (
        <>
            <PageHeader
                title={project.project_name}
                breadcrumbs={[
                    { label: 'Projects', to: '/projects' },
                    { label: project.project_code || 'Project' },
                ]}
                actions={
                    <>
                        <Button icon={Pencil} onClick={() => setIsEditModalOpen(true)}>
                            Edit
                        </Button>
                        <Button variant="danger" icon={Trash2} onClick={() => setIsDeleteModalOpen(true)}>
                            Delete
                        </Button>
                    </>
                }
            >
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <StatusBadge status={project.status} />
                    <PriorityBadge priority={project.priority} />
                    {project.project_code && (
                        <span className="text-label font-mono text-ink-500">{project.project_code}</span>
                    )}
                    <span className="text-ink-300" aria-hidden="true">·</span>
                    <span className="text-label text-ink-600 tabular">{project.progress}% complete</span>
                </div>
            </PageHeader>

            <Tabs tabs={TABS} value={activeTab} onChange={setActiveTab} className="mb-4" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2 space-y-4">
                    {activeTab === 'overview' && (
                        <>
                            <Card>
                                <CardHeader title="Description" />
                                <CardBody>
                                    {project.description ? (
                                        <p className="text-md text-ink-700 whitespace-pre-line leading-relaxed max-w-[75ch]">
                                            {project.description}
                                        </p>
                                    ) : (
                                        <p className="text-base text-ink-500">No description provided.</p>
                                    )}
                                </CardBody>
                            </Card>

                            <Card>
                                <CardHeader title="Project details" />
                                <CardBody className="divide-y divide-[var(--divider-color)] py-1">
                                    <DetailRow label="Start date">
                                        <span className="tabular">{formatDate(project.start_date)}</span>
                                    </DetailRow>
                                    <DetailRow label="Due date">
                                        <span className="tabular">{formatDate(project.end_date)}</span>
                                    </DetailRow>
                                    <DetailRow label="Budget">
                                        <span className="tabular">
                                            ${Number(project.budget || 0).toLocaleString()}
                                        </span>
                                    </DetailRow>
                                    <DetailRow label="Vendor">{project.vendor || 'Internal'}</DetailRow>
                                    <DetailRow label="Department">{project.department || 'General'}</DetailRow>
                                </CardBody>
                            </Card>
                        </>
                    )}

                    {activeTab === 'tasks' && (
                        <Card>
                            <CardHeader
                                title="Tasks"
                                subtitle={`${doneTasks} of ${tasks.length} complete`}
                                actions={
                                    <Link to="/tasks" className="btn btn-secondary btn-sm">
                                        Manage tasks
                                    </Link>
                                }
                            />
                            <CardBody>
                                {tasks.length ? (
                                    <div className="space-y-2">
                                        {tasks.map((task) => (
                                            <TaskCard key={task.id} task={task} />
                                        ))}
                                    </div>
                                ) : (
                                    <EmptyState
                                        compact
                                        title="No tasks yet"
                                        description="Break the project into tasks to start tracking progress."
                                    />
                                )}
                            </CardBody>
                        </Card>
                    )}

                    {activeTab === 'team' && (
                        <Card>
                            <CardHeader
                                title="Project team"
                                subtitle={`${project.team_members?.length || 0} members`}
                            />
                            {project.team_members?.length ? (
                                <ul className="divide-y divide-[var(--divider-color)]">
                                    {project.team_members.map((member) => (
                                        <li
                                            key={member.id}
                                            className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--bg-subtle)] transition-colors duration-150"
                                        >
                                            <Avatar name={member.member_name} size="md" />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-base font-medium text-ink-900 truncate">
                                                    {member.member_name}
                                                </p>
                                                <p className="text-micro text-ink-500 truncate">{member.email}</p>
                                            </div>
                                            {member.user_id === project.assignee_id ? (
                                                <Badge variant="primary">Project manager</Badge>
                                            ) : (
                                                <span className="text-label text-ink-500">
                                                    {member.role || 'Member'}
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <EmptyState
                                    compact
                                    title="No team members"
                                    description="Assign team members from the project settings."
                                />
                            )}
                        </Card>
                    )}

                    {activeTab === 'documents' && (
                        <Card>
                            <CardHeader
                                title="Documents"
                                subtitle={`${project.attachments?.length || 0} files`}
                            />
                            {project.attachments?.length ? (
                                <ul className="divide-y divide-[var(--divider-color)]">
                                    {project.attachments.map((file) => (
                                        <li
                                            key={file.id}
                                            className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--bg-subtle)] transition-colors duration-150"
                                        >
                                            <span
                                                aria-hidden="true"
                                                className="shrink-0 w-8 h-8 rounded-md bg-ink-100 text-ink-500 flex items-center justify-center"
                                            >
                                                <FileText size={15} />
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-base font-medium text-ink-900 truncate">
                                                    {file.file_name}
                                                </p>
                                                <p className="text-micro text-ink-500 truncate">
                                                    {Math.round(file.file_size / 1024)} KB · {file.uploaded_by_name} ·{' '}
                                                    {new Date(file.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <a
                                                href={fileUrl(file.file_path)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Open ${file.file_name}`}
                                                className="btn btn-ghost btn-sm btn-icon shrink-0"
                                            >
                                                <Download size={15} aria-hidden="true" />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <EmptyState
                                    compact
                                    icon={FileText}
                                    title="No documents"
                                    description="Attach contracts, specifications or sign-offs from the project form."
                                />
                            )}
                        </Card>
                    )}

                    {activeTab === 'comments' && (
                        <Card>
                            <CardHeader title="Comments" />
                            <CardBody>
                                <form onSubmit={handleAddComment} className="mb-5">
                                    <label htmlFor="project-comment" className="sr-only">
                                        Add a comment
                                    </label>
                                    <textarea
                                        id="project-comment"
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        className="input textarea"
                                        rows={3}
                                        placeholder="Add a comment…"
                                        required
                                    />
                                    <div className="flex justify-end mt-2">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            size="sm"
                                            loading={addCommentMutation.isPending}
                                        >
                                            Post comment
                                        </Button>
                                    </div>
                                </form>

                                {commentsLoading ? (
                                    <ListSkeleton rows={3} />
                                ) : comments?.length ? (
                                    <ol className="space-y-4">
                                        {comments.map((comment) => (
                                            <li key={comment.id} className="flex gap-2.5">
                                                <Avatar name={comment.user_name} size="md" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-baseline gap-2 flex-wrap">
                                                        <span className="text-base font-medium text-ink-900">
                                                            {comment.user_name}
                                                        </span>
                                                        <span className="text-micro text-ink-400 tabular">
                                                            {new Date(comment.created_at).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <p className="text-base text-ink-700 whitespace-pre-wrap mt-0.5 leading-relaxed">
                                                        {comment.comment}
                                                    </p>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                ) : (
                                    <EmptyState
                                        compact
                                        title="No comments yet"
                                        description="Use comments to record decisions and status updates."
                                    />
                                )}
                            </CardBody>
                        </Card>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                    <Card>
                        <CardHeader title="Progress" />
                        <CardBody className="space-y-4">
                            <ProgressBar value={project.progress} showValue />

                            <dl className="space-y-2 pt-1">
                                <div className="flex items-center justify-between">
                                    <dt className="text-base text-ink-600">Tasks</dt>
                                    <dd className="text-base font-medium text-ink-900 tabular">{tasks.length}</dd>
                                </div>
                                <div className="flex items-center justify-between">
                                    <dt className="text-base text-ink-600">Completed</dt>
                                    <dd className="text-base font-medium text-success-strong tabular">{doneTasks}</dd>
                                </div>
                                <div className="flex items-center justify-between">
                                    <dt className="text-base text-ink-600">Outstanding</dt>
                                    <dd className="text-base font-medium text-warning-strong tabular">
                                        {tasks.length - doneTasks}
                                    </dd>
                                </div>
                            </dl>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHeader title="Project manager" />
                        <CardBody>
                            {project.assignee_name ? (
                                <div className="flex items-center gap-2.5">
                                    <Avatar name={project.assignee_name} size="lg" />
                                    <div className="min-w-0">
                                        <p className="text-base font-medium text-ink-900 truncate">
                                            {project.assignee_name}
                                        </p>
                                        <p className="text-micro text-ink-500 truncate">{project.assignee_email}</p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-base text-ink-500">Unassigned</p>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>

            {isEditModalOpen && (
                <ProjectModal project={project} onClose={() => setIsEditModalOpen(false)} />
            )}

            <ConfirmDialog
                isOpen={isDeleteModalOpen}
                title="Delete project"
                message={`This permanently removes "${project.project_name}" and every task linked to it.`}
                confirmText="Delete project"
                variant="danger"
                isLoading={deleteProject.isPending}
                onConfirm={handleConfirmDelete}
                onClose={() => setIsDeleteModalOpen(false)}
            />
        </>
    );
};

export default ProjectDetail;
